'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';

import { FC, useMemo } from 'react';

import classNames from 'classnames';

import {
  Button,
  Modal,
  ModalCloseButton,
  ModalContent,
  ModalDescription,
  ModalHeading,
} from '@uikit';

import { MissingField, MissingFieldsEntry, MissingFieldsEntryDescription } from '@api';

import MagicSquareLogo from '@uikit/icons/MagicSquareLogo';

import { useCompleteMagicIdModal } from '@shared/common/providers/GlobalModalsProvider';
import useGlobalState from '@shared/common/providers/GlobalStateProvider';
import { nextProjectUrl } from '@shared/constants/urls';

import CompleteMagicIdModalBottomDecoration from '@store:web/components/common/modals/CompleteMagicIDModal/CompleteMagicIdModalBottomDecoration';
import CompleteMagicIdModalLeftDecoration from '@store:web/components/common/modals/CompleteMagicIDModal/CompleteMagicIdModalLeftDecoration';

const fieldValidationRules: Record<
  MissingField,
  (missingFields: MissingFieldsEntryDescription) => boolean
> = {
  EVM: (missingFields) => 'EVM' in (missingFields.wallets ?? {}),
  SOLANA: (missingFields) => 'SOLANA' in (missingFields.wallets ?? {}),
  avatar: (missingFields) => 'avatar' in missingFields,
  name: (missingFields) => 'name' in missingFields,
  email: (missingFields) => 'email' in missingFields,
  displayedName: (missingFields) => 'displayedName' in missingFields,
  humanityScore: (missingFields) =>
    'humanityScore' in missingFields && !missingFields.humanityScore?.acceptable,
};
const fieldPrompts: Record<MissingField, string> = {
  EVM: 'Add EVM wallet',
  SOLANA: 'Add SOLANA wallet',
  avatar: 'Choose a Avatar',
  email: 'Verify your E-mail',
  name: 'Choose a Username',
  displayedName: 'Choose a Displayed Name',
  humanityScore: 'Verify Humanity',
};

export const getIncompleteProfileMessages = (missingFieldsEntry: MissingFieldsEntry) => {
  const missingFields = missingFieldsEntry.description ?? {};

  return (Object.keys(fieldPrompts) as MissingField[]).filter((fieldName) => {
    const func = fieldValidationRules[fieldName];
    return func(missingFields);
  });
};

interface CompleteMagicIDModalProps {
  description?: string;
}

const CompleteMagicIDModal: FC<CompleteMagicIDModalProps> = ({
  description = 'In order to participate in the process, please complete the set-up of your MagicID',
}) => {
  const pathname = usePathname();
  const completeMagicIdModalController = useCompleteMagicIdModal();
  const { value: missingFieldsEntry, setValue: setMissingFields } =
    useGlobalState('missingFieldsEntry');
  const { urlToMagicId, incompleteProfileMessages } = useMemo(() => {
    if (!missingFieldsEntry) {
      return {
        urlToMagicId: '',
        incompleteProfileMessages: [],
      };
    }

    const messages = getIncompleteProfileMessages(missingFieldsEntry);
    const params = new URLSearchParams();
    const isOnlyWallet = 'wallets' in missingFieldsEntry.description && messages.length === 1;

    // will not open edit profile modal if it's only wallet that is absent
    // TODO: refactor logic for opening setting edit profile modal to hide implementation details
    if (!isOnlyWallet) {
      params.append('fillProfile', 'true');
    }

    params.append('from', pathname);

    const path = `/profile/settings${params ? `?${params}` : ''}`;
    const url = `${nextProjectUrl.store(path)}`;

    return {
      incompleteProfileMessages: messages,
      urlToMagicId: url,
    };
  }, [missingFieldsEntry, pathname]);

  if (!missingFieldsEntry) {
    return null;
  }

  const handleGoToSettingsClick = () => {
    setMissingFields(null);
    completeMagicIdModalController.close();
  };

  return (
    <Modal {...completeMagicIdModalController.props}>
      <ModalContent
        className={classNames(
          'rounded-xl p-0 flex-row border-none gap-0 md:w-[568px] 2xl:w-[830px] max-sm:mb-0',
        )}
        data-qa="vote-modal"
      >
        <CompleteMagicIdModalLeftDecoration />

        <div className="flex flex-col relative">
          <ModalCloseButton className="absolute top-0 right-0 text-secondary self-end hover:bg-secondary-100 transition rounded-full p-1 h-8 m-4" />

          <div
            className={classNames(
              'w-full h-full max-w-[270px] px-4 flex flex-col items-center justify-center',
              'sm:max-w-[410px] sm:pb-6 sm:px-6',
              'md:max-w-[490px]',
              '2xl:max-w-[550px] 2xl:px-[70px]',
            )}
          >
            <MagicSquareLogo className="icon-[4.375rem] my-6" />

            <ModalHeading>Complete your MagicID</ModalHeading>
            <ModalDescription>{description}</ModalDescription>

            {incompleteProfileMessages.length > 0 && (
              <ul className="mb-5">
                {incompleteProfileMessages.map((message) => (
                  <li
                    key={message}
                    className="flex items-center gap-1 leading-6"
                    data-qa="vote-check-field"
                  >
                    <div className="w-1 h-1 bg-red-600 rounded-full" />
                    {fieldPrompts[message]}
                  </li>
                ))}
              </ul>
            )}

            <Button
              variant="secondary"
              className="w-full font-semibold sm:self-end md:self-auto sm:w-[85%] md:w-full z-[1] border-none"
              as={Link as any}
              href={urlToMagicId}
              onClick={handleGoToSettingsClick}
            >
              To Magic ID
            </Button>
          </div>

          <CompleteMagicIdModalBottomDecoration />
        </div>
      </ModalContent>
    </Modal>
  );
};

export default CompleteMagicIDModal;
